import './defines';
import PageObject from 'classes/pageobject';
import {
	EditorCanvasPageObjectsModel,
	EditorInteractiveInteractionTemplatePhotoModel,
	EditorInteractiveInteractionTemplateTextModel,
	EditorInteractivePageObjectModel,
	EditorModulePageTemplatePositions,
} from 'interfaces/app';
import {
	AddressModel,
	OfferingFrameModel,
	OfferingModel,
} from 'interfaces/database';
import {
	PageModel,
	PageObjectModel,
} from 'interfaces/project';
import { OfferingGroups } from 'settings/offerings';
import { mobile as mobileTools } from 'tools';
import getColorInverse from 'tools/get-color-inverse';
import {
	color as colorUtils,
	object as objectUtils,
} from 'utils';
import EditorDrawView from 'views/editor-draw';
import EditorInteractiveView from 'views/editor-interactive-view';
import {
	Component,
	Prop,
	Ref,
	Vue,
	Watch,
} from 'vue-property-decorator';
import Template from './template.vue';

@Component({
	name: 'EditorCanvasView',
	components: {
		EditorDrawView,
		EditorInteractiveView,
	},
})
export default class EditorCanvasView extends Vue.extend(Template) {
	@Prop({
		default: undefined,
		schema: 'AddressModel',
		type: Object,
	})
	public readonly addressModel?: AddressModel;

	@Prop({
		default: false,
		description: 'Indicates if the eye dropper is shown, in which case the pointer events for the interactive component are disabled',
		event: 'eye-dropper-shown-change',
		type: Boolean,
	})
	public readonly isEyeDropperShown?: boolean;

	@Prop({
		default: false,
		description: 'Indicates if the editor canvas is interactive, meaning that the interactive component is enabled and the user can interact with the objects in the canvas, adding, editing and deleting them',
		type: Boolean,
	})
	public readonly isInteractive!: boolean;

	@Prop({
		description: 'Indicates if the photo object is transformable (resizable and rotatable)',
		required: true,
		type: Boolean,
	})
	public readonly isPhotoObjectTransformable!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly isTextOverlayActive!: boolean;

	@Prop({
		description: 'Indicates if the active product supports multiple photos in the page',
		required: true,
		type: Boolean,
	})
	public readonly multiplePhotoSupport!: boolean;

	@Prop({
		description: 'Indicates if the active product supports multiple texts in the page',
		required: true,
		type: Boolean,
	})
	public readonly multipleTextSupport!: boolean;

	@Prop({
		default: '#webapp',
		type: [HTMLElement, String],
	})
	public readonly objectActionsAnchor!: HTMLElement | string;

	@Prop({
		default: undefined,
		schema: 'OfferingFrameModel',
		type: Object,
	})
	public readonly offeringFrameModel?: OfferingFrameModel;

	@Prop({
		required: true,
		schema: 'OfferingModel',
		type: Object,
	})
	public readonly offeringModel!: OfferingModel;

	@Prop({
		required: true,
		type: Number,
	})
	public readonly pageIndex!: number;

	@Prop({
		event: 'page-model-change',
		required: true,
		schema: 'PageModel',
		type: Object,
	})
	public readonly pageModel!: PageModel;

	@Prop({
		default: () => ([]),
		event: 'page-objects-change',
		schema: 'EditorCanvasPageObjectsModel',
		type: Array,
	})
	public readonly pageObjects!: EditorCanvasPageObjectsModel;

	@Prop({
		default: () => ([]),
		schema: 'EditorModulePageTemplatePositions',
		type: Array,
	})
	public readonly pageTemplatePositionsAvailable!: EditorModulePageTemplatePositions;

	@Prop({
		required: true,
		type: Number,
	})
	public readonly scaling!: number;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly showAddress!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly showBleed!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly showEditorOfferingFrame!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly showMessage!: boolean;

	private get bleedMargin(): number {
		return (
			(
				this.showBleed
				&& this.pageModel.offset
			)
				? this.pageModel.offset
				: 0
		);
	}

	protected get bleedMarginStyles(): Partial<CSSStyleDeclaration> {
		const pageModelHeight = this.pageModel.height + (this.bleedMargin * 2);
		const pageModelWidth = this.pageModel.width + (this.bleedMargin * 2);

		return {
			boxShadow: `inset 0 0 0 ${this.bleedMargin * this.computedScaling}px rgba(255, 0, 0, 0.2)`,
			height: `${Math.round(pageModelHeight * this.computedScaling)}px`,
			width: `${Math.round(pageModelWidth * this.computedScaling)}px`,
		};
	}

	protected get computedClasses(): Record<string, boolean> {
		return {
			'editor-canvas-view-is-eye-dropper-shown': this.internalIsEyeDropperShown,
		};
	}

	private get computedObjectActionsAnchor(): HTMLElement | null {
		if (typeof this.objectActionsAnchor === 'string') {
			return document.querySelector(this.objectActionsAnchor);
		}
		if (this.objectActionsAnchor) {
			return this.objectActionsAnchor;
		}

		return document.getElementById('webapp');
	}

	protected get computedScaling(): number {
		if (
			this.showEditorOfferingFrame
			&& this.offeringFrameModel?.required
		) {
			const scaledFrameHeight = this.offeringFrameModel.frame_height * this.scaling;
			const scaledFrameWidth = this.offeringFrameModel.frame_width * this.scaling;

			return Math.min(
				scaledFrameWidth / this.pageModel.width,
				scaledFrameHeight / this.pageModel.height,
			);
		}

		return this.scaling;
	}

	protected get computedStyles(): Partial<CSSStyleDeclaration> {
		return {
			height: `${this.scaledCanvasHeight}px`,
			width: `${this.scaledCanvasWidth}px`,
		};
	}

	protected get drawViewsContainerStyles(): Partial<CSSStyleDeclaration> {
		const styles: Partial<CSSStyleDeclaration> = {};

		if (
			!this.showEditorOfferingFrame
			|| !this.offeringFrameModel
		) {
			styles.height = `${Math.round((this.pageModel.height + (this.bleedMargin * 2)) * this.scaling)}px`;
			styles.left = '0';
			styles.top = '0';
			styles.width = `${Math.round((this.pageModel.width + (this.bleedMargin * 2)) * this.scaling)}px`;
		} else {
			const height = this.offeringFrameModel.frame_height;
			const left = this.offeringFrameModel.frame_x;
			const top = this.offeringFrameModel.frame_y;
			const width = this.offeringFrameModel.frame_width;

			styles.height = `${Math.round(height * this.scaling)}px`;
			styles.left = `${Math.round(left * this.scaling)}px`;
			styles.top = `${Math.round(top * this.scaling)}px`;
			styles.width = `${Math.round(width * this.scaling)}px`;
		}

		return styles;
	}

	protected get flippedOverlay(): boolean {
		return !!(
			this.offeringModel
			&& OfferingGroups(
				this.offeringModel.groupid,
				['BookTypes'],
			)
			&& this.pageIndex % 2 === 0
		);
	}

	protected get internalPageObjectsOrdered(): EditorCanvasPageObjectsModel {
		return this.internalPageObjects.sort((a, b) => {
			if (a.z_axis < b.z_axis) {
				return -1;
			}
			if (a.z_axis > b.z_axis) {
				return 1;
			}

			return 1;
		});
	}

	protected get objectStyle(): (objectModel: PageObjectModel) => Partial<CSSStyleDeclaration> {
		return (objectModel) => {
			const vars = PageObject.calculatePosition(
				{
					x_axis: objectModel.x_axis,
					y_axis: objectModel.y_axis,
					width: objectModel.width,
					height: objectModel.height,
					borderwidth: objectModel.borderwidth,
					bordercolor: objectModel.bordercolor,
					cropx: objectModel.cropx,
					cropy: objectModel.cropy,
					cropwidth: objectModel.cropwidth,
					cropheight: objectModel.cropheight,
					angle: objectModel.rotate,
					flop: Boolean(objectModel.flop),
					flip: Boolean(objectModel.flip),
					type: objectModel.type,
					maxwidth: objectModel.maxwidth,
					photoid: objectModel.photoid || undefined,
				},
				this.computedScaling,
				this.pageModel,
				this.bleedMargin,
				this.offeringModel,
			);

			return {
				left: `${vars.canvas.left}px`,
				top: `${vars.canvas.top}px`,
			};
		};
	}

	private get offeringFrameHeight(): number {
		if (
			!this.showEditorOfferingFrame
			|| !this.offeringFrameModel
		) {
			return 0;
		}

		return Math.round(this.offeringFrameModel.height * this.scaling);
	}

	private get offeringFrameWidth(): number {
		if (
			!this.showEditorOfferingFrame
			|| !this.offeringFrameModel
		) {
			return 0;
		}

		return Math.round(this.offeringFrameModel.width * this.scaling);
	}

	protected get photoObjects(): EditorCanvasPageObjectsModel {
		return this.internalPageObjects.filter((objectModel) => objectModel.type === 'photo');
	}

	private get scaledCanvasHeight(): number {
		if (!this.offeringFrameHeight) {
			return Math.round(
				(this.pageModel.height + (this.bleedMargin * 2)) * this.scaling,
			);
		}

		return this.offeringFrameHeight;
	}

	private get scaledCanvasWidth(): number {
		if (!this.offeringFrameWidth) {
			return Math.round(
				(this.pageModel.width + (this.bleedMargin * 2)) * this.scaling,
			);
		}

		return this.offeringFrameWidth;
	}

	protected get selectedObjectActionsStyles(): Partial<CSSStyleDeclaration> {
		if (
			!this.$el
			|| !this.selectedObjectForEditionElement
			|| !this.selectedObjectActionsElement
		) {
			return {
				top: '0',
			};
		}

		const elementRect = this.$el.getBoundingClientRect();
		const selectedObjectForEditionElementRect = this.selectedObjectForEditionElement.getBoundingClientRect();
		const selectedObjectActionsElementRect = this.selectedObjectActionsElement.getBoundingClientRect();
		let distance = 24;
		let left = 0;
		let top = 0;

		if (
			this.offeringFrameModel?.required
			/**
			 * 5px of threshold to avoid the selected object actions to be
			 * displayed too close to the bottom of the offering frame.
			 */
			&& (this.offeringFrameModel.height - (this.offeringFrameModel.frame_y + this.offeringFrameModel.frame_height)) <= 5
			&& !mobileTools.isMobile
		) {
			distance = 16;
		}

		if (!mobileTools.isMobile) {
			left = selectedObjectForEditionElementRect.left + (selectedObjectForEditionElementRect.width / 2) - (selectedObjectActionsElementRect.width / 2);
		} else {
			left = elementRect.left + (elementRect.width / 2) - (selectedObjectActionsElementRect.width / 2);
		}

		if (!this.objectActionsAnchor) {
			top = elementRect.top + elementRect.height + distance;
		} else if (this.computedObjectActionsAnchor) {
			const anchorRect = this.computedObjectActionsAnchor.getBoundingClientRect();
			top = anchorRect.bottom - selectedObjectActionsElementRect.height - distance;
		}

		return {
			top: `${top}px`,
			left: `${left}px`,
		};
	}

	private get selectedObjectForEdition(): EditorInteractivePageObjectModel | undefined {
		return this.internalPageObjects.find((object) => !!object._selectedForEdition);
	}

	protected get shouldShowSelectedObjectActions(): boolean {
		const { selectedObjectForEdition } = this;

		if (!selectedObjectForEdition) {
			return false;
		}

		if (
			selectedObjectForEdition.type === 'photo'
			&& this.multiplePhotoSupport
		) {
			return true;
		}

		if (
			selectedObjectForEdition.type === 'text'
			&& this.multipleTextSupport
		) {
			return true;
		}

		return false;
	}

	protected get showOverlay(): boolean {
		if (this.offeringModel?.overlay) {
			return true;
		}

		if (
			this.offeringFrameModel?.required
			&& this.offeringFrameModel?.overpage
			&& !this.offeringModel?.mask
		) {
			return true;
		}

		return false;
	}

	@Ref('interactiveView')
	protected readonly interactiveViewComponent!: EditorInteractiveView;

	@Ref('mainCanvas')
	protected readonly mainCanvasComponent!: EditorDrawView;

	@Ref('selectedObjectActions')
	protected readonly selectedObjectActionsElement!: HTMLDivElement;

	private canvasElement: HTMLCanvasElement = document.createElement('canvas');

	private internalIsEyeDropperShown = false;

	private internalPageObjects: EditorCanvasPageObjectsModel = [];

	protected inverseBackgroundColor = getColorInverse(this.pageModel?.bgcolor || '#FFFFFF');

	private resizeObserver?: ResizeObserver;

	protected selectedObjectBorderColor = 'var(--primary1)';

	private selectedObjectForEditionElement: HTMLElement | null = null;

	protected beforeDestroy(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = undefined;
		window.removeEventListener(
			'click',
			this.onWindowClick,
		);
	}

	protected mounted(): void {
		this.resizeObserver = new ResizeObserver(() => {
			if (this.selectedObjectForEditionElement) {
				this.$forceCompute('selectedObjectActionsStyles');
			}
		});
		this.onPageModelOfferingFrameModelChange();
		this.canvasElement = this.mainCanvasComponent.getCanvasElement();
		this.$emit(
			'canvas-ready',
			this.canvasElement,
		);
	}

	@Watch(
		'isEyeDropperShown',
		{
			immediate: true,
		},
	)
	protected onIsEyeDropperShownChange(): void {
		this.internalIsEyeDropperShown = this.isEyeDropperShown || false;
	}

	@Watch(
		'offeringFrameModel',
		{
			deep: true,
		},
	)
	@Watch(
		'pageModel',
		{
			deep: true,
		},
	)
	protected onPageModelOfferingFrameModelChange(): void {
		if (
			!this.offeringFrameModel?.required
			&& this.pageModel
			&& this.$el
		) {
			const backgroundColor = this.pageModel.bgcolor || '#FFFFFF';
			const neutral4GreyColor = getComputedStyle(this.$el).getPropertyValue('--neutral4-grey');
			const white1Color = getComputedStyle(this.$el).getPropertyValue('--white1');
			const contrastNeutral4GreyColor = colorUtils.getContrastRatio(
				neutral4GreyColor,
				backgroundColor,
			);
			const contrastWhite1Color = colorUtils.getContrastRatio(
				white1Color,
				backgroundColor,
			);

			if (contrastNeutral4GreyColor < contrastWhite1Color) {
				this.inverseBackgroundColor = white1Color;
			} else {
				this.inverseBackgroundColor = neutral4GreyColor;
			}

			const primary1Color = getComputedStyle(this.$el).getPropertyValue('--primary1');
			const primary2Color = getComputedStyle(this.$el).getPropertyValue('--primary2');
			const contrastPrimary1Color = colorUtils.getContrastRatio(
				primary1Color,
				backgroundColor,
			);
			const contrastPrimary2Color = colorUtils.getContrastRatio(
				primary2Color,
				backgroundColor,
			);

			if (contrastPrimary1Color < contrastPrimary2Color) {
				this.selectedObjectBorderColor = primary2Color;
			} else {
				this.selectedObjectBorderColor = primary1Color;
			}
		} else if (
			this.offeringFrameModel?.required
			&& this.pageModel
			&& this.$el
		) {
			const { offeringFrameModel } = this;
			const image = new Image();
			image.crossOrigin = 'Anonymous';
			image.onload = () => {
				const dominantColor = colorUtils.getDominant({
					backgroundColor: this.pageModel.bgcolor || '#FFFFFF',
					region: {
						x: offeringFrameModel.frame_x,
						y: offeringFrameModel.frame_y,
						width: offeringFrameModel.frame_width,
						height: offeringFrameModel.frame_height,
					},
					sourceImage: image,
				});
				const neutral4GreyColor = getComputedStyle(this.$el).getPropertyValue('--neutral4-grey');
				const white1Color = getComputedStyle(this.$el).getPropertyValue('--white1');
				const contrastNeutral4GreyColor = colorUtils.getContrastRatio(
					neutral4GreyColor,
					dominantColor,
				);
				const contrastWhite1Color = colorUtils.getContrastRatio(
					white1Color,
					dominantColor,
				);

				if (contrastNeutral4GreyColor < contrastWhite1Color) {
					this.inverseBackgroundColor = white1Color;
				} else {
					this.inverseBackgroundColor = neutral4GreyColor;
				}

				const primary1Color = getComputedStyle(this.$el).getPropertyValue('--primary1');
				const primary2Color = getComputedStyle(this.$el).getPropertyValue('--primary2');
				const contrastPrimary1Color = colorUtils.getContrastRatio(
					primary1Color,
					dominantColor,
				);
				const contrastPrimary2Color = colorUtils.getContrastRatio(
					primary2Color,
					dominantColor,
				);

				if (contrastPrimary1Color < contrastPrimary2Color) {
					this.selectedObjectBorderColor = primary2Color;
				} else {
					this.selectedObjectBorderColor = primary1Color;
				}
			};
			image.src = `${this.offeringFrameModel.url}?noCorsHeader`;
		}
	}

	@Watch(
		'pageObjects',
		{
			deep: true,
			immediate: true,
		},
	)
	protected onPageObjectsChange(): void {
		const newObjects = this.pageObjects;
		const oldObjects = this.internalPageObjects;
		const objectsToRemove: EditorCanvasPageObjectsModel = [];

		// eslint-disable-next-line no-restricted-syntax
		for (const newObject of newObjects) {
			const oldObjectFound = oldObjects.find((oldObject) => oldObject.id === newObject.id);

			if (!oldObjectFound) {
				oldObjects.push({
					...newObject,
				});
			} else {
				const objectDifferences = objectUtils.getObjectDifferences(
					newObject,
					oldObjectFound,
				);

				if (objectDifferences.length) {
					// eslint-disable-next-line no-restricted-syntax
					for (const objectDifference of objectDifferences) {
						(oldObjectFound as any)[objectDifference] = newObject[objectDifference];
					}
				}
			}
		}

		// eslint-disable-next-line no-restricted-syntax
		for (const oldObject of oldObjects) {
			const newObjectFound = newObjects.find((newObject) => newObject.id === oldObject.id);

			if (!newObjectFound) {
				objectsToRemove.push(oldObject);
			}
		}

		// eslint-disable-next-line no-restricted-syntax
		for (const objectToRemove of objectsToRemove) {
			oldObjects.splice(
				oldObjects.indexOf(objectToRemove),
				1,
			);
		}
	}

	@Watch('selectedObjectForEdition')
	protected onSelectedObjectForEditionChange(): void {
		const { selectedObjectForEdition } = this;

		if (!selectedObjectForEdition) {
			this.selectedObjectForEditionElement = null;
			this.$forceCompute('selectedObjectActionsStyles');
			window.removeEventListener(
				'click',
				this.onWindowClick,
			);
			return;
		}

		this.$nextTick(() => {
			this.selectedObjectForEditionElement = this.interactiveViewComponent.$el.querySelector(`.editor-interactive-view-edit-object[data-object-id="${selectedObjectForEdition.id}"]`);
			this.$forceCompute('selectedObjectActionsStyles');
		});
		// this.selectedObjectForEditionElement = element;
		window.addEventListener(
			'click',
			this.onWindowClick,
		);
	}

	@Watch('shouldShowSelectedObjectActions')
	protected onShouldShowSelectedObjectActionsChange(): void {
		if (this.shouldShowSelectedObjectActions) {
			this.computedObjectActionsAnchor?.append(this.selectedObjectActionsElement);
		}
	}

	protected isPhotoObject(pageObject: PageObjectModel): boolean {
		return pageObject.type === 'photo';
	}

	protected onAddPhoto(templatePosition: EditorInteractiveInteractionTemplatePhotoModel['templatePosition']): void {
		this.$emit(
			'add-photo',
			templatePosition,
		);
	}

	protected onAddText(templatePosition: EditorInteractiveInteractionTemplateTextModel['templatePosition']): void {
		this.$emit(
			'add-text',
			templatePosition,
		);
	}

	protected onDeleteObject(event: MouseEvent | TouchEvent): void {
		event.preventDefault();
		this.$emit(
			'delete-object',
			this.selectedObjectForEdition,
		);
		this.selectedObjectForEditionElement = null;
	}

	protected onDrawObjectsChange(
		pageObjects: EditorCanvasPageObjectsModel,
		objectsDifferences: Record<PageObjectModel['id'], Array<keyof PageObjectModel>>,
	): void {
		const objectsChangedIds = Object.keys(objectsDifferences) as PageObjectModel['id'][];

		// eslint-disable-next-line no-restricted-syntax
		for (const objectId of objectsChangedIds) {
			const internalObjectFound = this.internalPageObjects.find((pageObject) => pageObject.id === objectId);
			const objectFound = pageObjects.find((pageObject) => pageObject.id === objectId);

			if (
				objectFound
				&& internalObjectFound
			) {
				const objectDifferences = objectsDifferences[objectId];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					(internalObjectFound as any)[objectDifference] = objectFound[objectDifference];
				}
			}
		}

		this.$emit(
			'page-objects-change',
			this.internalPageObjects,
			objectsDifferences,
		);
	}

	protected onEditorInteractiveChange(
		pageObjects: EditorCanvasPageObjectsModel,
		objectsDifferences: Record<PageObjectModel['id'], Array<keyof PageObjectModel>>,
	): void {
		const objectsChangedIds = Object.keys(objectsDifferences) as PageObjectModel['id'][];

		// eslint-disable-next-line no-restricted-syntax
		for (const objectId of objectsChangedIds) {
			const internalObjectFound = this.internalPageObjects.find((pageObject) => pageObject.id === objectId);
			const objectFound = pageObjects.find((pageObject) => pageObject.id === objectId);

			if (
				objectFound
				&& internalObjectFound
			) {
				const objectDifferences = objectsDifferences[objectId];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					(internalObjectFound as any)[objectDifference] = objectFound[objectDifference];
				}
			}
		}

		this.$emit(
			'page-objects-change',
			this.internalPageObjects,
			objectsDifferences,
		);
	}

	protected onEditorInteractiveDeleteObject(objectModel: EditorInteractivePageObjectModel): void {
		this.$emit(
			'delete-object',
			objectModel,
		);
		this.selectedObjectForEditionElement = null;
	}

	protected onEditorInteractiveEyeDropperShownChange(isEyeDropperShown: boolean): void {
		this.internalIsEyeDropperShown = isEyeDropperShown;
		this.$emit(
			'eye-dropper-shown-change',
			this.internalIsEyeDropperShown,
		);
	}

	protected onEditorInteractivePushChanges(): void {
		this.$emit('push-changes');
	}

	protected onEditObject(event: MouseEvent | TouchEvent): void {
		event.preventDefault();
		const { selectedObjectForEdition } = this;

		if (selectedObjectForEdition) {
			const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
				[selectedObjectForEdition.id]: [
					'_selectedForEdition',
					'_selected',
				],
			};
			selectedObjectForEdition._selectedForEdition = false;
			selectedObjectForEdition._selected = true;

			if (selectedObjectForEdition.type === 'text') {
				selectedObjectForEdition._previousState = {
					...selectedObjectForEdition,
				};
				objectsDifferences[selectedObjectForEdition.id].push('_previousState');
			}

			this.$emit(
				'page-objects-change',
				this.internalPageObjects,
				objectsDifferences,
			);
		}
	}

	protected onPageModelChange(pageModel: PageModel): void {
		this.$emit(
			'page-model-change',
			pageModel,
		);
	}

	private onWindowClick(event: MouseEvent | TouchEvent): void {
		const eventTarget = event.target as HTMLElement;

		if (
			this.selectedObjectActionsElement?.isSameNode(eventTarget)
			|| this.selectedObjectActionsElement?.contains(eventTarget)
		) {
			event.preventDefault();
		}
	}
}
